// ─────────────────────────────────────────────
//  roomDefsCampus — the department's published room list, PARTITIONED BY
//  CAMPUS, which is the form the importer needs and the tab does not supply.
//
//  ⚠️ THIS DOES NOT READ THE TAB. `parseRoomDefs` in
//  features/sheet-import/roomDefs.js is the ONE reader of `Room Defs`; this
//  takes its output and answers the question it does not — which hospital.
//  Pure — no Firebase, no React, no network.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────

import {
  CAMPUS_RULES,
  ENDO_ALIAS_FOLD,
  areaKey,
  splitArea,
  foldArea,
} from "./roomCampusRules";

// ──────────────────────────────────────
//  crossCampusAreaIndex
//
//  Derive — from the department's own published room list, not from a guess —
//  which bare area words exist at more than one campus. Those are the labels a
//  typist can send to the wrong hospital by omitting a prefix.
//
//  @param {string[]} roomNames canonical room names (Room Defs `names`, or any
//                              live room-doc name list)
//  @returns {Map<string, Array<{facilityId:string, name:string}>>} areaKey → siblings
// ──────────────────────────────────────
export function crossCampusAreaIndex(roomNames) {
  const list = Array.isArray(roomNames) ? roomNames : [];
  const byArea = new Map();
  for (const name of list) {
    if (/\btbd\b/i.test(String(name ?? ""))) continue; // the bench is campus-neutral
    const split = splitArea(name);
    if (!split) continue;
    const key = areaKey(foldArea(split.area));
    if (!key) continue;
    if (!byArea.has(key)) byArea.set(key, []);
    const bucket = byArea.get(key);
    if (!bucket.some((e) => e.facilityId === split.facilityId && e.name === name)) {
      bucket.push({ facilityId: split.facilityId, name });
    }
  }
  return byArea;
}

// ──────────────────────────────────────
//  roomDefsCampusIndex — the department's published list, PARTITIONED BY
//  CAMPUS, which is the form the importer actually needs and the tab does not
//  supply (`Room Defs` has no campus column; the campus is in the name and,
//  corroborating it, in the Epic prefix).
//
//  Two lookups, and the difference between them is the whole incident:
//    byName   — case-insensitive, spacing PRESERVED. `WC 5` ≠ `WC5`.
//    bySquash — spacing ignored. Used ONLY to name a candidate on a label that
//               nothing claimed as typed, never to override a verbatim answer.
// ──────────────────────────────────────
//  ⚠️ THIS DOES NOT READ THE TAB. `parseRoomDefs` in
//  features/sheet-import/roomDefs.js is the ONE reader of `Room Defs`; this
//  takes its output and answers the question it does not — which hospital.
//  Accepts `parseRoomDefs(values)`, its `.rooms`, or a plain name array.
//
//  ⚠️ THE EPIC COLUMN IS A SECOND, INDEPENDENT WITNESS. AHTAM/TAM = Tampa,
//  AHCAR/CAR = Carrollwood, AHWCH/WCH = Wesley Chapel. Where it speaks it
//  corroborates the name rule; where the two DISAGREE the room is indexed on
//  NEITHER campus and lands in `conflicts`. It cannot decide alone —
//  `EP LAB 1`, `CATH LAB 10`, `CCL/EP LAB` and seven blank cells carry no
//  campus at all.
// ──────────────────────────────────────
const EPIC_CAMPUS_PREFIXES = [
  { re: /^ahtam\b|^tam\s/i, facilityId: "tampa" },
  { re: /^ahcar\b|^car\s/i, facilityId: "carrollwood" },
  { re: /^ahwch\b|^wch\s/i, facilityId: "default" },
];

function epicCampuses(epicNames) {
  const out = new Set();
  (Array.isArray(epicNames) ? epicNames : []).forEach((n) => {
    const hit = EPIC_CAMPUS_PREFIXES.find((x) => x.re.test(String(n ?? "").trim()));
    if (hit) out.add(hit.facilityId);
  });
  return out;
}

export function roomDefsCampusIndex(parsed) {
  const roomsIn = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.rooms)
      ? parsed.rooms
      : Array.isArray(parsed?.names)
        ? parsed.names
        : [];
  const rooms = roomsIn.map((r) =>
    typeof r === "string" ? { prelimName: r, epicNames: [] } : r || {}
  );

  const byName = new Map();
  const bySquash = new Map();
  const unclaimed = [];
  const conflicts = [];
  for (const room of rooms) {
    const raw = String(room.prelimName ?? "").trim();
    if (!raw) continue;
    const rules = CAMPUS_RULES.filter((r) => r.pattern.test(raw));
    const fids = [...new Set(rules.map((r) => r.facilityId))];
    if (fids.length !== 1) {
      // A published room our own campus table cannot place, or places twice.
      if (!/\btbd\b/i.test(raw)) unclaimed.push({ name: raw, facilityIds: fids });
      continue;
    }
    // ⚠️ ONE PRELIM NAME, SEVERAL EPIC ROOMS IS NORMAL. `CT`, `MRI`, `NI`,
    // `CAR TEE` and `CAR E2` each map to two or three Epic rooms, and one cell
    // holds two separated by a newline. That is not ambiguity — it is one room
    // label the case feed spells several ways, and it collapses to ONE campus.
    // It is a conflict only when those Epic names name DIFFERENT hospitals.
    const epic = epicCampuses(room.epicNames);
    if (epic.size > 1 || (epic.size === 1 && !epic.has(fids[0]))) {
      conflicts.push({
        name: raw,
        byName: fids[0],
        byEpic: [...epic],
        epicNames: room.epicNames || [],
      });
      continue; // never index a room the two witnesses disagree about
    }
    const entry = {
      facilityId: fids[0],
      name: raw,
      ruleId: rules[0].id,
      epicNames: Array.isArray(room.epicNames) ? room.epicNames : [],
      corroboratedByEpic: epic.size === 1,
    };
    const nk = raw.toLowerCase().replace(/\s+/g, " ");
    if (!byName.has(nk)) byName.set(nk, entry);
    const sk = areaKey(raw);
    if (!bySquash.has(sk)) bySquash.set(sk, []);
    if (!bySquash.get(sk).some((e) => e.name === entry.name)) bySquash.get(sk).push(entry);
  }
  return {
    byName,
    bySquash,
    unclaimed,
    conflicts,
    areas: crossCampusAreaIndex(rooms.map((r) => r.prelimName)),
  };
}

// ──────────────────────────────────────
//  crossCampusRiskFor — is this bare label a word another campus also uses?
//
//  ⚠️ THIS DOES NOT CHANGE THE ANSWER. It attaches the reason a correct-looking
//  Tampa answer might still be wrong, so a review queue can ask. Silently
//  moving the case would be the worse error.
// ──────────────────────────────────────
export function crossCampusRiskFor(label, crossCampusAreas) {
  if (!(crossCampusAreas instanceof Map)) return null;
  const raw = String(label ?? "").trim();
  if (!raw) return null;
  const split = splitArea(raw);
  // Only a label with NO campus prefix is at risk. "WC MRI" says which campus.
  if (!split || split.facilityId !== "tampa") return null;
  const key = areaKey(foldArea(split.area));
  const siblings = crossCampusAreas.get(key);
  if (!siblings) return null;
  const campuses = new Set(siblings.map((s) => s.facilityId));
  if (campuses.size < 2) return null;
  return {
    area: key,
    siblings,
    why:
      `The same area word is published at ${campuses.size} campuses ` +
      `(${siblings.map((s) => s.name).join(", ")}). Typed without a prefix it resolves ` +
      "to Tampa, and the Carrollwood and Wesley Chapel tabs print this exact word as " +
      "their own section header, so a local-dialect entry cannot be told from a Tampa one.",
    evidence: ENDO_ALIAS_FOLD.evidence,
  };
}
